import React from 'react';
import { useTranslation } from 'react-i18next';
import './onboarding-stepper.style.scss';

/**
 * Shared onboarding stepper header shown across the whole flow so the separate
 * step surfaces read as one connected wizard: Profile → Bank → Email → Verify → Finish.
 * The Verify step is dropped (`hideVerify`) for users who registered via Bank ID
 * or are already verified, matching the flow's skip logic.
 */
export type OnboardingStepKey = 'profile' | 'bank' | 'email' | 'verify' | 'finish';

interface OnboardingStepperProps {
  active: OnboardingStepKey;
  hideVerify?: boolean;
  /** Steps already satisfied — shown with a checkmark even if they are the active step. */
  completed?: OnboardingStepKey[];
  className?: string;
}

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OnboardingStepper: React.FC<OnboardingStepperProps> = ({ active, hideVerify, completed = [], className = '' }) => {
  const { t } = useTranslation();

  const steps: { key: OnboardingStepKey; label: string }[] = [
    { key: 'profile', label: t('COMPLETE_PROFILE') || 'Profile' },
    { key: 'bank', label: t('BANK_DETAILS') || 'Bank details' },
    { key: 'email', label: t('EMAIL_VERIFICATION') || 'Email verification' },
    ...(!hideVerify ? [{ key: 'verify' as OnboardingStepKey, label: t('ID_VERIFICATION') || 'Verification' }] : []),
    { key: 'finish', label: t('FINISH') || 'Finish' },
  ];

  const activeIndex = steps.findIndex((s) => s.key === active);

  return (
    <div className={`onboarding-stepper ${className}`.trim()} role="list" aria-label="Onboarding progress">
      {steps.map((step, i) => {
        const isActive = i === activeIndex;
        const isDone = completed.includes(step.key) || i < activeIndex;
        // Active step wins the highlight; a completed step (incl. the active one) shows a check.
        const state = isActive ? 'active' : isDone ? 'done' : 'upcoming';
        return (
          <React.Fragment key={step.key}>
            <div className={`onboarding-stepper__step is-${state}`} role="listitem" aria-current={isActive}>
              <span className="onboarding-stepper__bullet">
                {isDone ? <CheckIcon /> : <span className="onboarding-stepper__num">{i + 1}</span>}
              </span>
              <span className="onboarding-stepper__label">{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <span
                className={`onboarding-stepper__line ${i < activeIndex || completed.includes(step.key) ? 'is-done' : ''}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default OnboardingStepper;
